import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Loader2, ArrowLeft, Wand2, LayoutTemplate } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import api from "@/lib/api";

const TONES = [["peer", "Peer"], ["formal", "Formal"], ["technical", "Technical"]];

export function BriefDialog({ variant = "default", label = "New from brief", className = "", sourceOffer = null }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("input");
  const [busy, setBusy] = useState(false);
  const [rewriting, setRewriting] = useState(-1);
  const [rwOpen, setRwOpen] = useState(-1);
  const [rwNote, setRwNote] = useState("");
  const [form, setForm] = useState({ what_we_sell: "", who_buys: "", proof: "", motion: "private", booking_url: "", tone: "peer" });
  const [draft, setDraft] = useState(null);
  const [offerId, setOfferId] = useState(null);
  const [offerEdited, setOfferEdited] = useState(false);
  const [templates, setTemplates] = useState([]);
  const navigate = useNavigate();

  const reset = () => { setStep("input"); setDraft(null); setOfferEdited(false); setOfferId(sourceOffer?.id || null);
    setForm({ what_we_sell: sourceOffer ? `${sourceOffer.product || ""} — ${sourceOffer.features || ""}` : "",
      who_buys: sourceOffer?.icp?.icp_brief || "", proof: sourceOffer?.proof || "",
      motion: sourceOffer?.sales_strategy === "public-sector procurement" ? "public" : "private",
      booking_url: "", tone: "peer" }); };
  const onOpenChange = (o) => { setOpen(o); if (o) { reset(); api.get("/templates").then((r) => setTemplates(r.data)).catch(() => {}); } };

  const setMotion = (m) => setForm({ ...form, motion: m, tone: m === "public" ? "formal" : form.tone });

  const generate = async () => {
    if (!form.what_we_sell.trim()) return toast.error("Tell us what you sell");
    setBusy(true);
    try { const { data } = await api.post("/brief/generate", { ...form, offer_id: offerId }); setDraft(data); setOfferId(data.offer_id || offerId); setStep("review"); }
    catch (e) { toast.error(e?.response?.data?.detail?.message || "Could not generate the brief"); } finally { setBusy(false); }
  };

  const startFromTemplate = (t) => {
    setDraft({ offer: t.offer_snapshot || {}, strategy: {}, motion: t.type === "public_sector" ? "public" : "private",
      tone: t.tone || "peer", campaign: { name: t.name, summary: t.description, steps: (t.steps || []).filter((s) => (s.channel || "email") === "email") },
      linkedin: t.linkedin_template || {} });
    setOfferId(null); setOfferEdited(true); setStep("review");
  };

  const setStepField = (i, key, val) => setDraft((d) => { const c = structuredClone(d); c.campaign.steps[i][key] = val; return c; });
  const rewriteStep = async (i, note) => {
    setRewriting(i);
    try {
      const s = draft.campaign.steps[i];
      const { data } = await api.post("/brief/rewrite-step", { subject: s.subject || "", body: s.body || "", tone: draft.tone || form.tone, proof: draft.offer?.proof || "", motion: draft.motion || form.motion, note: note || "" });
      setDraft((d) => { const c = structuredClone(d); c.campaign.steps[i].subject = data.subject; c.campaign.steps[i].body = data.body; return c; });
      toast.success("Step rewritten"); setRwOpen(-1); setRwNote("");
    } catch (e) { toast.error(e?.response?.data?.detail?.message || "Rewrite failed"); } finally { setRewriting(-1); }
  };

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/brief/save", { offer: draft.offer, strategy: draft.strategy, campaign: draft.campaign,
        linkedin: draft.linkedin, motion: draft.motion, tone: draft.tone || form.tone, offer_id: offerId, offer_edited: offerEdited });
      toast.success("Saved — approve the template and enroll your first 5 samples");
      onOpenChange(false); navigate(`/app/campaigns/${data.campaign_id}?enroll=1`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(d?.message || "Could not save", { duration: d?.code === "proof_required" ? 7000 : 4000 });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button variant={variant} className={`rounded-md ${className}`} data-testid="new-from-brief-btn"><Sparkles className="h-4 w-4 mr-1.5" /> {label}</Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="brief-dialog">
        {step === "input" ? (
          <>
            <DialogHeader>
              <DialogTitle>{sourceOffer ? "New sequence from this offer" : "Create from a brief"}</DialogTitle>
              <DialogDescription>One screen in. We draft your offer, strategy, email sequence and a LinkedIn task — all editable before you save. Nothing sends and no one is enrolled automatically.</DialogDescription>
            </DialogHeader>
            {!!templates.length && (
              <div className="rounded-lg border border-border/60 bg-card/40 p-3" data-testid="template-picker">
                <div className="text-xs font-medium mb-2 flex items-center gap-1.5"><LayoutTemplate className="h-3.5 w-3.5" /> Start from a workspace template</div>
                <div className="flex flex-wrap gap-1.5">
                  {templates.map((t) => <Button key={t.id} size="sm" variant="outline" className="rounded-md text-xs" onClick={() => startFromTemplate(t)} data-testid={`template-${t.id}`}>{t.name}</Button>)}
                </div>
              </div>
            )}
            <div className="space-y-4">
              <div>
                <Label className="text-xs">What we sell <span className="text-muted-foreground">· plain language</span></Label>
                <Textarea value={form.what_we_sell} onChange={(e) => setForm({ ...form, what_we_sell: e.target.value })} rows={2} className="mt-1.5" data-testid="brief-what" />
              </div>
              <div>
                <Label className="text-xs">Who buys <span className="text-muted-foreground">· optional</span></Label>
                <Input value={form.who_buys} onChange={(e) => setForm({ ...form, who_buys: e.target.value })} className="mt-1.5" data-testid="brief-who" />
              </div>
              <div>
                <Label className="text-xs">Proof we're allowed to say <span className="text-muted-foreground">· optional; empty = no proof in copy</span></Label>
                <Textarea value={form.proof} onChange={(e) => setForm({ ...form, proof: e.target.value })} rows={2} className="mt-1.5" data-testid="brief-proof" />
              </div>
              <div>
                <Label className="text-xs">Motion</Label>
                <div className="flex gap-2 mt-1.5">
                  <Button type="button" variant={form.motion === "private" ? "default" : "outline"} className="rounded-md flex-1" onClick={() => setMotion("private")} data-testid="brief-motion-private">Private-sector SaaS</Button>
                  <Button type="button" variant={form.motion === "public" ? "default" : "outline"} className="rounded-md flex-1" onClick={() => setMotion("public")} data-testid="brief-motion-public">Public-sector procurement</Button>
                </div>
              </div>
              <div>
                <Label className="text-xs">Tone {form.motion === "public" && <span className="text-amber-500">· locked to Formal for public-sector</span>}</Label>
                <div className="flex gap-2 mt-1.5">
                  {TONES.map(([v, l]) => <Button key={v} type="button" disabled={form.motion === "public" && v !== "formal"} variant={form.tone === v ? "default" : "outline"} className="rounded-md flex-1" onClick={() => setForm({ ...form, tone: v })} data-testid={`brief-tone-${v}`}>{l}</Button>)}
                </div>
              </div>
              <div>
                <Label className="text-xs">Booking URL <span className="text-muted-foreground">· default workspace URL</span></Label>
                <Input value={form.booking_url} onChange={(e) => setForm({ ...form, booking_url: e.target.value })} className="mt-1.5" placeholder="Leave blank to use your workspace booking link" data-testid="brief-booking" />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={generate} disabled={busy} className="rounded-md bg-amber-500 text-black hover:bg-amber-400" data-testid="brief-generate-btn">{busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5" />} Generate drafts</Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Review & edit drafts</DialogTitle>
              <DialogDescription>Everything here is a draft. Edit anything, then save. {draft.motion === "public" && <span className="text-amber-500">Public-sector: a source URL is required before any live send.</span>}</DialogDescription>
            </DialogHeader>
            <div className="space-y-5">
              <section className="rounded-lg border border-border/60 p-4">
                <div className="eyebrow mb-2">Offer {offerId && !offerEdited && <span className="text-muted-foreground normal-case">· from existing offer (won't change unless you edit)</span>}</div>
                <Label className="text-xs">Product</Label>
                <Input value={draft.offer.product || ""} onChange={(e) => { setDraft({ ...draft, offer: { ...draft.offer, product: e.target.value } }); setOfferEdited(true); }} className="mt-1 mb-2" data-testid="brief-offer-product" />
                <Label className="text-xs">What it does</Label>
                <Textarea value={draft.offer.features || ""} onChange={(e) => { setDraft({ ...draft, offer: { ...draft.offer, features: e.target.value } }); setOfferEdited(true); }} rows={2} className="mt-1" />
              </section>
              {draft.strategy?.icp_brief && (
                <section className="rounded-lg border border-border/60 p-4">
                  <div className="eyebrow mb-2">Strategy</div>
                  <p className="text-xs text-muted-foreground mb-2">{draft.strategy.icp_brief}</p>
                  {!!(draft.strategy?.titles || []).length && <div className="flex flex-wrap gap-1.5 mb-1">{draft.strategy.titles.slice(0, 8).map((t, i) => <Badge key={i} variant="outline" className="text-xs">{t}</Badge>)}</div>}
                  {!!(draft.strategy?.do_not_invent || []).length && <p className="text-[11px] text-amber-500 mt-2">Guardrails: {draft.strategy.do_not_invent.join("; ")}</p>}
                </section>
              )}
              <section className="rounded-lg border border-border/60 p-4">
                <div className="eyebrow mb-2">Sequence · {draft.campaign?.name}</div>
                {(draft.campaign?.steps || []).map((s, i) => (
                  <div key={i} className="mb-3 pb-3 border-b border-border/40 last:border-0" data-testid={`brief-step-${i}`}>
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="text-xs font-medium">Day {s.day} · Email</span>
                      <Badge className={`text-[10px] border-0 ${i === 0 ? "bg-amber-500/15 text-amber-500" : "bg-sky-500/15 text-sky-500"}`}>{i === 0 ? "Needs approval" : "Auto after send"}</Badge>
                      <Button size="sm" variant="ghost" className="rounded-md h-6 text-[11px] ml-auto" onClick={() => { setRwOpen(rwOpen === i ? -1 : i); setRwNote(""); }} disabled={rewriting === i} data-testid={`brief-rewrite-${i}`}>{rewriting === i ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Wand2 className="h-3 w-3 mr-1" />} Rewrite this step</Button>
                    </div>
                    {rwOpen === i && (
                      <div className="mb-2 flex items-center gap-2" data-testid={`brief-rewrite-note-row-${i}`}>
                        <Input value={rwNote} onChange={(e) => setRwNote(e.target.value)} placeholder="shorter / more urgent / no jargon" className="text-sm h-8 flex-1" data-testid={`brief-rewrite-note-input-${i}`} onKeyDown={(e) => { if (e.key === "Enter") rewriteStep(i, rwNote); }} />
                        <Button size="sm" className="rounded-md h-8 bg-amber-500 text-black hover:bg-amber-400" onClick={() => rewriteStep(i, rwNote)} disabled={rewriting === i} data-testid={`brief-rewrite-note-submit-${i}`}>Rewrite</Button>
                        <Button size="sm" variant="ghost" className="h-8" onClick={() => { setRwOpen(-1); setRwNote(""); }} data-testid={`brief-rewrite-note-cancel-${i}`}>Cancel</Button>
                      </div>
                    )}
                    <Input value={s.subject || ""} onChange={(e) => setStepField(i, "subject", e.target.value)} className="mb-1.5 text-sm" placeholder="Subject" data-testid={`brief-step-subject-${i}`} />
                    <Textarea value={s.body || ""} onChange={(e) => setStepField(i, "body", e.target.value)} rows={4} className="text-sm" data-testid={`brief-step-body-${i}`} />
                  </div>
                ))}
              </section>
              <section className="rounded-lg border border-border/60 p-4">
                <div className="eyebrow mb-2">LinkedIn task · send by hand, due +{draft.linkedin?.due_at_offset_days ?? 2}d</div>
                <Textarea value={draft.linkedin?.note || ""} onChange={(e) => setDraft({ ...draft, linkedin: { ...draft.linkedin, note: e.target.value } })} rows={3} className="text-sm" data-testid="brief-linkedin-note" />
              </section>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" className="rounded-md" onClick={() => setStep("input")}><ArrowLeft className="h-4 w-4 mr-1.5" /> Back</Button>
              <Button onClick={save} disabled={busy} className="rounded-md bg-amber-500 text-black hover:bg-amber-400" data-testid="brief-save-btn">{busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null} Save drafts</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
